/* ============================================================
   Zampe & Cuori — Wishlist (localStorage MVP).

   - Storage versionato: 'zc-wishlist-v1'. Format: array di oggetti
     {id, handle, title, url, image, price, compare_at_price}.
     Denormalizzazione deliberata: la pagina /pages/i-miei-preferiti
     renderizza le card senza fetch verso Shopify, leggendo tutto
     dal localStorage. Trade-off: se un prodotto viene aggiornato o
     eliminato in admin, la wishlist mostra dato stale fino al
     re-save dell'utente. Accettabile per MVP.
   - Migrazione vecchio key 'zc-wishlist' (array di string IDs):
     scartato silently — feature nuova, perdita accettabile.
   - API globale: window.zcWishlist.{getAll, add, remove, has, count, clear}
   - Eventi DOM su window: 'zc:wishlist:add', 'zc:wishlist:remove'
     (detail: { productId, count, item })
   - Cross-tab sync via storage event.
   - Toast notification auto-trigger su add/remove.
   - Bind automatico di tutti i [data-zc-wishlist] presenti nel DOM
     (es. cuoricino PDP) — leggono i data-product-* attribute per
     denormalizzare il salvataggio.
   ============================================================ */
(function () {
  'use strict';

  const STORAGE_KEY = 'zc-wishlist-v1';
  const OLD_KEY = 'zc-wishlist';
  const TOAST_DURATION = 2500;

  // ---- Storage helpers ------------------------------------------------

  function readList() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // Quota / private mode — fail silent.
    }
  }

  function migrate() {
    // Vecchio formato (array di string IDs) → scartato. Se l'utente
    // aveva 1-5 elementi, perde — accettabile per feature nuova.
    if (localStorage.getItem(OLD_KEY) && !localStorage.getItem(STORAGE_KEY)) {
      try {
        localStorage.removeItem(OLD_KEY);
      } catch (e) {
        /* noop */
      }
    }
  }

  migrate();

  // ---- Public API -----------------------------------------------------

  function getAll() {
    return readList();
  }

  function has(productId) {
    const sid = String(productId);
    return readList().some((item) => String(item.id) === sid);
  }

  function count() {
    return readList().length;
  }

  function add(item) {
    if (!item || !item.id) return false;
    const list = readList();
    const sid = String(item.id);
    if (list.some((i) => String(i.id) === sid)) return false;
    // Normalize: ensure id is string
    const normalized = Object.assign({}, item, { id: sid });
    list.push(normalized);
    writeList(list);
    emit('zc:wishlist:add', { productId: sid, count: list.length, item: normalized });
    return true;
  }

  function remove(productId) {
    const sid = String(productId);
    const list = readList();
    const idx = list.findIndex((i) => String(i.id) === sid);
    if (idx < 0) return false;
    const removed = list.splice(idx, 1)[0];
    writeList(list);
    emit('zc:wishlist:remove', { productId: sid, count: list.length, item: removed });
    return true;
  }

  function clear() {
    writeList([]);
    emit('zc:wishlist:remove', { productId: null, count: 0, item: null });
  }

  function emit(name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  // Expose
  window.zcWishlist = {
    getAll: getAll,
    has: has,
    count: count,
    add: add,
    remove: remove,
    clear: clear,
  };

  // ---- Toast ----------------------------------------------------------

  let activeToast = null;
  let toastTimer = null;

  function showToast(message) {
    // Rimuovi toast attivo (se l'utente aggiunge/rimuove velocemente).
    if (activeToast) {
      activeToast.remove();
      clearTimeout(toastTimer);
    }
    const el = document.createElement('div');
    el.className = 'zc-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = '<span class="zc-toast__icon" aria-hidden="true">♡</span><span class="zc-toast__text"></span>';
    el.querySelector('.zc-toast__text').textContent = message;
    document.body.appendChild(el);
    // Force reflow per attivare il transition CSS dell'opacity/transform.
    void el.offsetWidth;
    el.classList.add('is-visible');
    activeToast = el;
    toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => {
        el.remove();
        if (activeToast === el) activeToast = null;
      }, 200);
    }, TOAST_DURATION);
  }

  window.addEventListener('zc:wishlist:add', () => showToast('Aggiunto ai preferiti'));
  window.addEventListener('zc:wishlist:remove', (e) => {
    // Skip toast per clear() (productId null).
    if (e.detail && e.detail.productId) showToast('Rimosso dai preferiti');
  });

  // ---- Bind buttons ---------------------------------------------------

  function bindButton(btn) {
    if (btn.dataset.zcBound === '1') return;
    btn.dataset.zcBound = '1';

    const id = btn.dataset.productId;
    if (!id) return;

    syncButtonState(btn);

    btn.addEventListener('click', (event) => {
      event.preventDefault();
      if (has(id)) {
        remove(id);
      } else {
        // Leggi i data attributes denormalizzati dal Liquid per
        // salvare snapshot completo del prodotto.
        const item = {
          id: id,
          handle: btn.dataset.productHandle || '',
          title: btn.dataset.productTitle || '',
          url: btn.dataset.productUrl || '',
          image: btn.dataset.productImage || '',
          price: btn.dataset.productPrice ? parseInt(btn.dataset.productPrice, 10) : null,
          compare_at_price: btn.dataset.productCompareAtPrice
            ? parseInt(btn.dataset.productCompareAtPrice, 10)
            : null,
        };
        add(item);
      }
    });
  }

  function syncButtonState(btn) {
    const id = btn.dataset.productId;
    if (!id) return;
    const active = has(id);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('is-active', active);
  }

  function bindAll() {
    document.querySelectorAll('[data-zc-wishlist]').forEach(bindButton);
  }

  // ---- Header badge update -------------------------------------------

  function updateHeaderBadge() {
    const badges = document.querySelectorAll('[data-zc-wishlist-count]');
    const n = count();
    badges.forEach((badge) => {
      badge.textContent = String(n);
      if (n > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
  }

  // ---- Wire up on DOM ready ------------------------------------------

  function init() {
    bindAll();
    updateHeaderBadge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync su eventi wishlist (per quando il DOM cambia o un altro
  // tab modifica lo storage).
  window.addEventListener('zc:wishlist:add', () => {
    document.querySelectorAll('[data-zc-wishlist]').forEach(syncButtonState);
    updateHeaderBadge();
  });
  window.addEventListener('zc:wishlist:remove', () => {
    document.querySelectorAll('[data-zc-wishlist]').forEach(syncButtonState);
    updateHeaderBadge();
  });

  // Cross-tab sync via storage event.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    document.querySelectorAll('[data-zc-wishlist]').forEach(syncButtonState);
    updateHeaderBadge();
  });
})();
